import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Conversation } from '../types/conversation';

interface ConversationsContextType {
  conversations: Conversation[];
  addConversation: (conversation: Omit<Conversation, 'id'>) => void;
  updateConversationReview: (chatId: string, reviewId: string, reviewStatus: Conversation['reviewStatus']) => void;
  updateConversationAnswer: (conversationId: string, answer: string) => void;
}

const ConversationsContext = createContext<ConversationsContextType | undefined>(undefined);

export const useConversations = () => {
  const context = useContext(ConversationsContext);
  if (!context) {
    throw new Error('useConversations must be used within ConversationsProvider');
  }
  return context;
};

interface ConversationsProviderProps {
  children: ReactNode;
}

export const ConversationsProvider: React.FC<ConversationsProviderProps> = ({ children }) => {
  const [conversations, setConversations] = useState<Conversation[]>([
    // Hardcoded interactions - Steven's conversation
    {
      id: 'conv-steven-2',
      chatId: 'interaction-steven',
      title: 'Engineering Requirements for Product Integration',
      askedBy: 'Steven',
      question: 'Umer, what are the current engineering requirements when integrating a new product?',
      answer: '**Engineering Integration Requirements (Platform-Level)**\n\nNew product features must align with approved platform APIs and service contracts\n\nSecurity, performance, and reliability reviews are required before production release\n\nFeature work must comply with platform versioning and deployment guidelines\n\nIntegration timelines depend on existing roadmap commitments and capacity\n\n**Additional context from Umer:**\n\nPlatform capacity is currently constrained due to ongoing roadmap commitments\n\nNew integrations are being prioritized based on dependency impact and delivery risk\n\nEarly alignment with platform engineers helps reduce downstream delays\n\n**Knowledge Scope:** Owner-verified engineering context (Umer) and organization-published engineering standards\n**Confidence:** High (90%)\n\n**Sources:**\n• Umer\'s OneDrive – Platform Roadmap Notes\n• SharePoint – Engineering Integration Guidelines\n• SharePoint – Engineering Planning Updates\n• OneDrive – Platform API Standards',
      answeredAt: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
      confidenceLevel: 90,
      reviewRequested: false,
      mentionedPerson: 'Umer',
      sources: [
        {
          type: 'onedrive',
          title: 'Umer\'s OneDrive – Platform Roadmap Notes',
          url: 'https://adnoc-my.sharepoint.com/personal/omar/Documents/platform-roadmap',
        },
        {
          type: 'sharepoint',
          title: 'SharePoint – Engineering Integration Guidelines',
          url: 'https://adnoc.sharepoint.com/sites/engineering/integration-guidelines',
        },
        {
          type: 'sharepoint',
          title: 'SharePoint – Engineering Planning Updates',
          url: 'https://adnoc.sharepoint.com/sites/engineering/planning-updates',
        },
        {
          type: 'onedrive',
          title: 'OneDrive – Platform API Standards',
          url: 'https://adnoc-my.sharepoint.com/personal/engineering/Documents/platform-api-standards',
        },
      ],
    },
    {
      id: 'conv-steven-3',
      chatId: 'interaction-steven',
      title: 'Integration Commitment for Next Release',
      askedBy: 'Steven',
      question: '@Umer, can we commit to supporting this integration in the next release cycle?',
      answer: '**Decision Requires Human Alignment**\n\nI can\'t confirm this commitment confidently.\n\nCommitting to a specific release cycle would affect platform delivery plans, team capacity, and existing priorities. This decision is not defined in published standards or owner-verified documentation.\n\nTo avoid speculative guidance, this request has been routed for human review.\n\n**Decision Scope:** Cross-team planning\n**Confidence:** Not applicable (decision-level)\n\n**Actions Taken:**\n• Umer has been notified to review this request\n• Human alignment is required before providing guidance',
      answeredAt: new Date(Date.now() - 1000 * 60 * 60), // 1 hour ago
      confidenceLevel: undefined,
      reviewRequested: true,
      reviewStatus: 'pending',
      reviewId: 'review-steven-3',
      mentionedPerson: 'Umer',
    },
  ]);

  const addConversation = useCallback((conversation: Omit<Conversation, 'id'>) => {
    const newConversation: Conversation = {
      ...conversation,
      id: `conv-${Date.now()}`,
    };
    setConversations(prev => [newConversation, ...prev]);
  }, []);

  const updateConversationReview = useCallback((
    chatId: string,
    reviewId: string,
    reviewStatus: Conversation['reviewStatus']
  ) => {
    setConversations(prev =>
      prev.map(conv =>
        conv.chatId === chatId && conv.reviewId === reviewId
          ? { ...conv, reviewStatus }
          : conv
      )
    );
  }, []);

  const updateConversationAnswer = useCallback((conversationId: string, answer: string) => {
    setConversations(prev =>
      prev.map(conv =>
        conv.id === conversationId
          ? { 
              ...conv, 
              answer,
              reviewRequested: false,
              reviewStatus: 'approved',
            }
          : conv
      )
    );
  }, []);

  return (
    <ConversationsContext.Provider
      value={{
        conversations,
        addConversation,
        updateConversationReview,
        updateConversationAnswer,
      }}
    >
      {children}
    </ConversationsContext.Provider>
  );
};

